import React, { useEffect, useState } from 'react';
import { Banknote, TrendingUp, TrendingDown, RefreshCw, Plus, CreditCard } from 'lucide-react';
import { api } from '../../services/apiClient';

interface Transaction {
  id: number;
  type: 'revenue' | 'guide_payment' | 'expense' | 'refund';
  amount: number;
  description: string | null;
  category: string | null;
  payment_date: string | null;
  invoice_reference: string | null;
  is_settled: boolean;
  created_at: string;
}

interface FinanceSummary {
  totalRevenue: number;
  totalExpenses: number;
  totalRefunds: number;
  balance: number;
}

const TYPE_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  revenue: { label: 'Wpłata', color: 'text-emerald-600 bg-emerald-50', icon: <TrendingUp size={12} /> },
  guide_payment: { label: 'Wypłata przewodnik', color: 'text-blue-600 bg-blue-50', icon: <TrendingDown size={12} /> },
  expense: { label: 'Koszt', color: 'text-orange-600 bg-orange-50', icon: <TrendingDown size={12} /> },
  refund: { label: 'Zwrot', color: 'text-red-600 bg-red-50', icon: <RefreshCw size={12} /> },
};

export const TripFinancePanel: React.FC<{ applicationId: number }> = ({ applicationId }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<FinanceSummary>({ totalRevenue: 0, totalExpenses: 0, totalRefunds: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: 'revenue' as string, amount: '', description: '', category: '', invoice_reference: '' });

  const load = () => {
    api.get(`/wycieczki/application/${applicationId}/finances`)
      .then(r => {
        const txns = r.data.transactions || [];
        setTransactions(txns);
        const rev = txns.filter((t: Transaction) => t.type === 'revenue').reduce((s: number, t: Transaction) => s + Number(t.amount), 0);
        const exp = txns.filter((t: Transaction) => t.type === 'expense' || t.type === 'guide_payment').reduce((s: number, t: Transaction) => s + Number(t.amount), 0);
        const ref = txns.filter((t: Transaction) => t.type === 'refund').reduce((s: number, t: Transaction) => s + Number(t.amount), 0);
        setSummary({ totalRevenue: rev, totalExpenses: exp, totalRefunds: ref, balance: rev - exp - ref });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [applicationId]);

  const handleAdd = async () => {
    if (!form.amount || !form.type) return;
    setSaving(true);
    try {
      await api.post(`/wycieczki/application/${applicationId}/finances`, {
        type: form.type,
        amount: parseFloat(form.amount),
        description: form.description || null,
        category: form.category || null,
        invoice_reference: form.invoice_reference || null,
      });
      setForm({ type: 'revenue', amount: '', description: '', category: '', invoice_reference: '' });
      setShowForm(false);
      load();
    } catch { /* */ }
    setSaving(false);
  };

  const fmtPLN = (v: number) => v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return null;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Banknote size={18} className="text-white" />
          <h3 className="text-white font-bold text-sm">Finanse zgłoszenia</h3>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="text-white/80 hover:text-white p-1 rounded"
          title="Dodaj transakcję"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-2 p-4">
        <div className="bg-emerald-50 rounded-lg p-2 text-center">
          <p className="text-[10px] text-emerald-600 uppercase font-medium">Przychody</p>
          <p className="text-sm font-bold text-emerald-700">{fmtPLN(summary.totalRevenue)}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-2 text-center">
          <p className="text-[10px] text-orange-600 uppercase font-medium">Koszty</p>
          <p className="text-sm font-bold text-orange-700">{fmtPLN(summary.totalExpenses)}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-2 text-center">
          <p className="text-[10px] text-red-600 uppercase font-medium">Zwroty</p>
          <p className="text-sm font-bold text-red-700">{fmtPLN(summary.totalRefunds)}</p>
        </div>
        <div className={`rounded-lg p-2 text-center ${summary.balance >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
          <p className={`text-[10px] uppercase font-medium ${summary.balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>Bilans</p>
          <p className={`text-sm font-bold ${summary.balance >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{fmtPLN(summary.balance)}</p>
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="px-4 pb-3 space-y-2 border-t border-gray-100 pt-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500">Typ</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm">
                <option value="revenue">Wpłata klienta</option>
                <option value="guide_payment">Wypłata przewodnik</option>
                <option value="expense">Koszt operacyjny</option>
                <option value="refund">Zwrot klientówi</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Kwota (PLN)</label>
              <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" step="0.01" min="0.01" placeholder="0.00" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">Opis</label>
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" placeholder="np. Wpłata za wycieczkę Kombinat" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500">Nr faktury</label>
              <input type="text" value={form.invoice_reference} onChange={e => setForm(f => ({ ...f, invoice_reference: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" placeholder="FV/2026/..." />
            </div>
            <div>
              <label className="text-xs text-gray-500">Kategoria</label>
              <input type="text" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" placeholder="transport, catering..." />
            </div>
          </div>
          <button onClick={handleAdd} disabled={saving || !form.amount} className="w-full bg-blue-600 text-white py-1.5 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Zapisywanie...' : 'Dodaj transakcję'}
          </button>
        </div>
      )}

      {/* Transaction list */}
      {transactions.length > 0 ? (
        <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
          {transactions.map(tx => {
            const meta = TYPE_LABELS[tx.type] || TYPE_LABELS.expense;
            return (
              <div key={tx.id} className="px-4 py-2.5 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.color}`}>
                    {meta.icon} {meta.label}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 truncate">{tx.description || '—'}</p>
                    <p className="text-[10px] text-gray-400">
                      {tx.payment_date ? new Date(tx.payment_date).toLocaleDateString('pl-PL') : new Date(tx.created_at).toLocaleDateString('pl-PL')}
                      {tx.invoice_reference && ` · ${tx.invoice_reference}`}
                    </p>
                  </div>
                </div>
                <span className={`text-sm font-bold whitespace-nowrap ml-2 ${tx.type === 'revenue' ? 'text-emerald-600' : 'text-gray-700'}`}>
                  {tx.type === 'revenue' ? '+' : '-'}{fmtPLN(Number(tx.amount))} zł
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-4 py-6 text-center text-sm text-gray-400">
          <CreditCard size={24} className="mx-auto mb-2 text-gray-300" />
          Brak transakcji finansowych
        </div>
      )}
    </div>
  );
};
